import React, { useState, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { IUserJson } from '@scale/shared/types/user';
import { CircularProgress, Button } from 'react-scale/components/design-system';
import API from '@scale/frontend-lib/dashboard-api';
import { useMount } from 'react-use-v13';
import { ScreeningPendingReview } from '@scale/shared/taskerScreening/types';
import Table from 'corp/components/UI/Table';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { ScreeningReviewQualificationSectionStatus } from '@scale/shared/types/qualification/types';
import TaskerScreeningGradingReviewModal from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningGradingReviewModal';
import TaskerScreeningGradingFiltersModal, {
  TaskerScreeningWorkerSkillThresholds,
} from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningGradingFiltersModal';
import { AUDITOR_DOCUMENTATION_LINK } from 'react-scale/corp/views/rapid/TaskerScreeningUtils/TaskerScreeningUtils';

interface TaskerScreeningGradingProps {
  user: IUserJson;
  isCorp: boolean;
}

const tableHeaders: Record<string, string> = {
  worker: 'Worker',
  workerEmail: 'Worker Email',
  workerSkillName: 'Skill',
  workerSkillId: 'Skill ID',
  createdAt: 'Last Answered',
  course: 'Course',
  status: 'Status',
  sectionTitle: 'Course Section Title',
  action: 'Grade Answer',
  score: 'Saved Grade',
  workerSkillEntryScore: 'Existing Worker Skill Score',
};

const shownTableHeaders = [
  'worker',
  'workerSkillName',
  'action',
  'createdAt',
  'score',
  'workerSkillEntryScore',
];

const hiddenInRemoadminTableHeaders = ['workerSkillEntryScore', 'worker', 'score'];

const filterableTableHeaders = [
  'worker',
  'workerSkillName',
  'createdAt',
  'score',
  'workerSkillEntryScore',
];

const TaskerScreeningGrading: React.FC<TaskerScreeningGradingProps> = ({ user, isCorp }) => {
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [reviews, setReviews] = useState<ScreeningPendingReview[]>([]);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [actionReview, setActionReview] = useState<ScreeningPendingReview>();
  const [totalBacklog, setTotalBacklog] = useState<number | undefined>();

  // filters related
  const [openFiltersModal, setOpenFiltersModal] = useState(false);
  const [showLlmGraded, setShowLlmGraded] = useState(false);
  const [filterAllowUndefinedScore, setFilterAllowUndefinedScore] = useState(false);
  const [filterSkillThresholds, setFilterSkillThresholds] =
    useState<TaskerScreeningWorkerSkillThresholds>({});

  const loadTable = async () => {
    setLoadingTable(true);
    setTotalBacklog(undefined);
    const { reviews: fetchedReviews, totalPossibleTaskerReviewsCount } =
      await API.genai.fetchPendingScreening({});
    setReviews(fetchedReviews);
    setTotalBacklog(totalPossibleTaskerReviewsCount);
    setLoadingTable(false);
    const workerSkillNames = _.compact(
      _.uniq(fetchedReviews.map(review => review.workerSkillName)),
    );
    const defaultFilterSkillThresholds: TaskerScreeningWorkerSkillThresholds = {};
    for (const workerSkillName of workerSkillNames) {
      defaultFilterSkillThresholds[workerSkillName] = null;
    }
    setFilterSkillThresholds(defaultFilterSkillThresholds);
  };

  useMount(async () => {
    await loadTable();
  });

  const updateReviewInDatabase = async (score: number, reviewId: string, feedback?: string) => {
    const { newScore } = await API.genai.reviewScreeningWithScore({
      score,
      id: reviewId,
      feedback,
    });
    return newScore;
  };

  const handleScoreReview = useCallback(
    async ({ score, feedback }: { score: number; feedback?: string }) => {
      if (!actionReview) return;
      setLoadingUpdate(true);
      setOpenActionModal(false);
      const newScore = await updateReviewInDatabase(score / 1000, actionReview._id, feedback);
      const newReviews = _.compact(
        reviews.map(r => {
          if (r._id === actionReview._id) {
            return null;
          }
          if (r.worker === actionReview.worker && r.workerSkillId === actionReview.workerSkillId) {
            return {
              ...r,
              workerSkillEntryScore: newScore,
            };
          }
          return r;
        }),
      );
      setReviews(newReviews);
      setActionReview(undefined);
      setLoadingUpdate(false);
      setTotalBacklog(totalBacklog ? totalBacklog - 1 : undefined);
    },
    [actionReview, reviews, totalBacklog],
  );

  const tableData = useMemo(() => {
    let data = reviews.map(review => ({
      workerSkillId: review.workerSkillId,
      workerSkillName: review.workerSkillName,
      worker: <SmartLink type={INamedObjectTypes.User} id={review.worker} />,
      workerEmail: review.workerEmail,
      action: (
        <Button
          onClick={() => {
            setActionReview(review);
            setOpenActionModal(true);
          }}
          type={isCorp && review.isBenchmarkAudit ? 'warning' : 'secondary'}
          disabled={loadingUpdate}
        >
          Grade {isCorp && review.isBenchmarkAudit ? 'Benchmark' : 'Answer'}
        </Button>
      ),
      createdAt: review.createdAt.toString().split('T')[0],
      course: review.course,
      status: review.status,
      score:
        review.status === ScreeningReviewQualificationSectionStatus.AutoGraded
          ? `${review.score} (LLM-Graded)`
          : `${review.score ?? ''}`,
      workerSkillEntryScore:
        _.isNumber(review.workerSkillEntryScore) && review.workerSkillEntryScore >= 0
          ? review.workerSkillEntryScore
          : undefined,
    }));
    // filtering
    if (!showLlmGraded) {
      data = data.filter(d => d.status !== ScreeningReviewQualificationSectionStatus.AutoGraded);
    }
    data = data.filter(d => {
      const workerSkillScores: TaskerScreeningWorkerSkillThresholds = {};
      for (const review of reviews) {
        if (review.workerEmail === d.workerEmail && review.workerSkillName) {
          workerSkillScores[review.workerSkillName] = review.workerSkillEntryScore ?? null;
        }
      }
      for (const skill of Object.keys(filterSkillThresholds)) {
        if (_.isNumber(filterSkillThresholds[skill])) {
          if (filterAllowUndefinedScore && workerSkillScores[skill] === null) {
            continue;
          }
          if ((workerSkillScores[skill] ?? 0) < (filterSkillThresholds[skill] as number)) {
            return false;
          }
        }
      }
      return true;
    });
    return data;
  }, [
    filterAllowUndefinedScore,
    filterSkillThresholds,
    isCorp,
    loadingUpdate,
    reviews,
    showLlmGraded,
  ]);

  const columns = useMemo(() => {
    return Object.keys(tableHeaders).map(label => ({
      Header: tableHeaders[label],
      accessor: label,
      show: isCorp
        ? shownTableHeaders.includes(label)
        : shownTableHeaders.includes(label) && !hiddenInRemoadminTableHeaders.includes(label),
      filterable: filterableTableHeaders.includes(label),
    }));
  }, [isCorp]);

  return (
    <div>
      {loadingTable ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
            <CircularProgress />
            <p>This may take a few seconds...</p>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 12 }}>
            <Button style={{ marginRight: 12 }} onClick={() => setOpenFiltersModal(true)}>
              Open Filters
            </Button>
            <Button style={{ marginRight: 12 }} onClick={loadTable}>
              Refresh Table
            </Button>
            {!isCorp && (
              // redundant if in corp
              <Button
                onClick={() => {
                  window.open(AUDITOR_DOCUMENTATION_LINK, '_blank');
                }}
              >
                Open Documentation
              </Button>
            )}
          </div>

          {totalBacklog !== undefined && (
            <div style={{ marginBottom: 8 }}>
              You are currently permissioned to review <strong>{totalBacklog}</strong> audits. We
              sample and serve these in the table below, but you can refresh your table to claim and
              review more audits.
            </div>
          )}

          <Table
            filterable
            data={tableData}
            columns={columns}
            defaultFilterMethod={(filter, row) => {
              const rowValue =
                filter.id === 'worker'
                  ? `${row[filter.id].props.id}${row.workerEmail}`
                  : filter.id === 'workerSkillName'
                  ? `${row[filter.id]}${row.workerSkillName}`
                  : row[filter.id];
              return (rowValue ?? '').toLowerCase().includes(filter.value.toLowerCase());
            }}
          />
          {!reviews.length && (
            <p>
              Click "Refresh Table". If you still see no audits, reach out to an operator for help.
            </p>
          )}
          {actionReview && openActionModal && (
            <TaskerScreeningGradingReviewModal
              review={actionReview}
              open={openActionModal}
              setOpen={setOpenActionModal}
              updateReviewScore={handleScoreReview}
              _id={actionReview._id}
              loggedUserId={user._id}
            />
          )}
          <div className="scale-ui scale-theme scale-tailwind">
            <TaskerScreeningGradingFiltersModal
              open={openFiltersModal}
              onClose={() => setOpenFiltersModal(false)}
              showLlmGraded={showLlmGraded}
              setShowLlmGraded={setShowLlmGraded}
              filterSkillThresholds={filterSkillThresholds}
              setFilterSkillThresholds={setFilterSkillThresholds}
              filterAllowUndefinedScore={filterAllowUndefinedScore}
              setFilterAllowUndefinedScore={setFilterAllowUndefinedScore}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskerScreeningGrading;
